/**
 * The Board's code block. A ```chart block is drawn as a figure through the
 * chat's own chart pipeline (`parseChartSpec` sanitises the option, then
 * `EChartsFigure` draws it), so a chart the AI puts on the Board looks and
 * behaves as it does in a chat answer. Editors flip a chart to its JSON
 * source and back; every other language stays an editable code block.
 *
 * The node keeps StarterKit's name and attributes (`codeBlock`,
 * `language`), so it shares the server's headless schema
 * (`@amoreg/shared/spaceBoard`).
 *
 * @module components/spaces/board/BoardCodeBlock
 */
import CodeBlock from "@tiptap/extension-code-block";
import {
  NodeViewContent,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  type ReactNodeViewProps,
} from "@tiptap/react";
import { BOARD_CHART_LANGUAGES } from "@amoreg/shared/spaceBoard";
import { BarChart3Icon, CodeIcon } from "lucide-react";
import { useMemo, useState } from "react";

import { useTheme } from "../../../hooks/useTheme";
import { EChartsFigure } from "../../chat/EChartsFigure";
import { parseChartSpec } from "../../chat/chartSpec";

const isChartLanguage = (language: unknown): boolean =>
  typeof language === "string" && BOARD_CHART_LANGUAGES.has(language.trim().toLowerCase());

function BoardCodeBlockView({ node, editor }: ReactNodeViewProps) {
  const chart = isChartLanguage(node.attrs.language);
  const source = node.textContent;
  const spec = useMemo(() => (chart ? parseChartSpec(source) : null), [chart, source]);
  const [showSource, setShowSource] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const { resolvedTheme } = useTheme();
  const drawable = spec !== null && failed !== source;

  if (!chart) {
    return (
      <NodeViewWrapper as="pre" className="space-board-code" data-language={node.attrs.language ?? ""}>
        <NodeViewContent<"code"> as="code" />
      </NodeViewWrapper>
    );
  }

  const sourceVisible = showSource || !drawable;
  return (
    <NodeViewWrapper className="space-board-figure" data-board-chart="" data-drag-handle="">
      {drawable ? (
        <div contentEditable={false} className={sourceVisible ? "hidden" : undefined}>
          <EChartsFigure
            spec={spec}
            mode={resolvedTheme === "dark" ? "dark" : "light"}
            onFailure={() => setFailed(source)}
          />
        </div>
      ) : null}
      <div
        contentEditable={false}
        className="space-board-figure-bar"
        role="toolbar"
        aria-label="Chart actions"
      >
        <span className="inline-flex items-center gap-1.5 text-muted-foreground">
          <BarChart3Icon className="size-3.5" />
          {drawable ? (spec.title ?? "Chart") : "Chart source is not valid JSON yet"}
        </span>
        {editor.isEditable && drawable ? (
          <button
            type="button"
            className="space-board-figure-button"
            aria-pressed={showSource}
            onClick={() => setShowSource((value) => !value)}
          >
            <CodeIcon className="size-3" />
            {showSource ? "Show chart" : "Edit source"}
          </button>
        ) : null}
      </div>
      {/* The text stays in the document either way; only its visibility changes. */}
      <pre className={sourceVisible ? "space-board-code" : "hidden"} data-language="chart">
        <NodeViewContent<"code"> as="code" />
      </pre>
    </NodeViewWrapper>
  );
}

export const BoardCodeBlock = CodeBlock.extend({
  addNodeView() {
    return ReactNodeViewRenderer(BoardCodeBlockView);
  },
});
